/**
 * Door — Phase 2, hosted into walls (hostId + hostOffsetFt along the host's centerline).
 * Shape variants are DATA (Addendum 2, docs/bim-plan/01 §4): conditional TYPE params +
 * generator branches — no family editor. Vertical extent: level base + sillFt →
 * sillFt + heightFt (+ headRiseFt arch above for arched/segmental heads) — the same
 * sillFt convention windows already use (2026-08-09, Matt: shower doors, bifold
 * pass-throughs set at counter height, mechanical-closet doors on a platform).
 * Per-instance, not per-type (the same physical door type gets placed both at the
 * floor and raised).
 *
 * OPERATION (doors/windows round, 2026-08): a TYPE param — a Pocket 2'-8" is a curated
 * firm-standard type, and operation drives real prototype geometry so it must fork
 * protoKeyFor (which hashes type params only). It derives the PLAN SYMBOL and the
 * ELEVATION GLYPH (operationSymbols.ts in bim-geometry) — representation is DERIVED,
 * never stored. Tier 2 values + the reserved `panelCount`/`parkMode` params are
 * documented in ../openingOps.ts; adding them is values + defs, never a reshape.
 *
 * OPENING CONFIGURATION GOVERNANCE round (2026-08-10, docs/MODELER.md — supersedes the
 * prior "sizes stay type params" call for doors/windows, applying the CASEWORK TYPE/
 * INSTANCE GOVERNANCE precedent): TYPE = the product FORM (operation + frame profile +
 * construction + glazing). widthFt/heightFt moved to INSTANCE — many sizes per type,
 * constrained to the curated `sizeSeries` catalog below (the palette renders a size
 * dropdown + a flagged "Custom size…" escape hatch). Muntin pattern/rows/cols also
 * moved to instance — "the same window with/without grilles is one type" (Matt's exact
 * example, stated for windows; applied symmetrically here for a full-lite door's
 * grille, which is exactly the same kind of dressing, not a different product). Leg B
 * width (corner slider) and panel/park-mode configuration stay TYPE — not named in the
 * governance decision, and a genuinely different construction per the same reasoning
 * `legBFt` was TYPE originally. protoKeyFor forks the instancing cache on the migrated
 * instance params (bim-geometry/categories/opening/protos.ts, the casework-width
 * precedent) since they still shape the prototype MESH itself.
 */
import type { CategorySchema } from '../../registry'
import { EXTERIOR_TRIM_TYPE_GROUP, EXTERIOR_TRIM_INSTANCE_PARAM, BRICK_LINTEL_INSTANCE_PARAM, INTERIOR_TRIM_INSTANCE_PARAMS } from '../../exteriorTrim'
import {
  DOOR_OPERATIONS, DOOR_OPS_WITH_FACING, DOOR_OPS_WITH_HAND,
  DOOR_OPS_WITH_DIRECTION, DOOR_OPS_WITH_PANEL_COUNT, DOOR_OPS_WITH_PARK_MODE,
  DOOR_OPS_WITH_HANDLES, HANDLE_TYPES, GATE_OPS, DOOR_OPS_WITH_HEAD_SHAPE, DOOR_HEAD_TYPES, DOOR_OPS_WITH_SHUTTERS,
} from '../openingOps'
import { HARDWARE_LOOKS, DOOR_HARDWARE_FINISHES } from '../../doorHardware'

/** SITE GATES: the door-only params (frames, glazing, sidelights, thresholds) hide on a
 *  gate — every operation that is NOT a gate */
const NON_GATE_OPS: string[] = DOOR_OPERATIONS.map((o) => o.value as string).filter((v) => !GATE_OPS.includes(v))
const notGate = { param: 'operation', equals: NON_GATE_OPS }
const gateOnly = { param: 'operation', equals: [...GATE_OPS] }
/** the operations whose leaves take a panel style (openingStyle.ts PANEL_FREE_OPS is the other half) */
const PANEL_FREE: string[] = ['multiSlide', 'multiBifold', 'cornerSlider', 'sectional', 'schweiss', 'casedOpening', ...GATE_OPS]
const takesPanel = { param: 'operation', equals: DOOR_OPERATIONS.map((o) => o.value as string).filter((v) => !PANEL_FREE.includes(v)) }

export const doorSchema: CategorySchema = {
  id: 'door',
  pickRank: 0,
  label: 'Door',
  hostable: { hostsInto: ['wall', 'fence'], cutsHost: true },
  typeParams: {
    groups: [
      {
        label: 'Dimensions',
        params: [
          // OPENING CONFIGURATION GOVERNANCE: the curated standard-size catalog for
          // this form/operation — an editable table in the type editor (SizeSeriesTable),
          // consumed by the instance-side size dropdown (OpeningSizeField). `hidden`
          // because it has a dedicated interaction, not a generic form row (the wall
          // `flipped` convention). Empty = no curated series yet, instances fall back to
          // free-entry width/height with nothing to flag as a deviation.
          { key: 'sizeSeries', label: 'Standard Sizes', kind: 'data', default: [], hidden: true },
          // DOOR REALISM round (2026-08-21, docs/bim-plan/16 §3 — Matt's resolved Q1):
          // frame width AND depth are curated make/model entries (the sizeSeries
          // pattern — a selector, never type proliferation), resolved through
          // openingFrame.ts. Empty = the built-in default (the pre-round constants),
          // so existing types are untouched. `hidden` — a dedicated table
          // (FrameSeriesTable) edits it, like sizeSeries.
          { key: 'frameSeries', label: 'Frame Products', kind: 'data', default: [], hidden: true },
          {
            // corner slider only: the LEG B clear width (the instance widthFt is leg A)
            key: 'legBFt', label: 'Leg B Width', kind: 'length', default: 6, min: 2, max: 30,
            conditionalOn: { param: 'operation', equals: 'cornerSlider' },
          },
        ],
      },
      {
        label: 'Operation',
        params: [
          {
            key: 'operation', label: 'Operation', kind: 'choice', default: 'hinged',
            choices: DOOR_OPERATIONS.map((o) => ({ value: o.value, label: o.label })),
          },
          // Tier 2 (2026-08-08): the reserved multi-panel params, exactly as documented
          // in openingOps.ts — values + defs, no reshape. Tier-1 slider/bifold stay the
          // fixed 2-panel members of their families.
          // PANEL COUNT PER INSTANCE (2026-10-06, Matt: "change the number of panels from a
          // type parameter to an instance parameter"): the type's counts are now only the
          // STARTING count for a new placement (hidden here) — each door's own Panel Count
          // lives on the instance (below), folded back over these in doc.ts
          {
            key: 'panelCount', label: 'Panel Count', kind: 'number', default: 3, min: 2, max: 8, hidden: true,
            conditionalOn: { param: 'operation', equals: [...DOOR_OPS_WITH_PANEL_COUNT] },
          },
          {
            // corner slider only: leg B's panel count (panelCount above is leg A)
            key: 'panelCountB', label: 'Leg B Panel Count', kind: 'number', default: 2, min: 1, max: 8, hidden: true,
            conditionalOn: { param: 'operation', equals: 'cornerSlider' },
          },
          {
            // 'stack' = panels bunch at the jamb(s) inside the opening plane;
            // 'pocket' = panels park INTO the wall — the derived dashed cavity in
            // plan, sized to the parked stack (multiSlide + cornerSlider)
            key: 'parkMode', label: 'Park Mode', kind: 'choice', default: 'stack',
            choices: [
              { value: 'stack', label: 'Stack (At the Jamb)' },
              { value: 'pocket', label: 'Pocket (Into the Wall)' },
            ],
            conditionalOn: { param: 'operation', equals: [...DOOR_OPS_WITH_PARK_MODE] },
          },
        ],
      },
      {
        // SITE GATES (2026-10-02 — openingOps.ts GATE_OPS): the leaf's construction,
        // all firm-standard TYPE params (a picket-over-panel gate with 3/4" bars is one
        // curated product). Built by bim-geometry opening/gate.ts, which the 3D leaf,
        // the elevation figure and the plan symbol all share.
        label: 'Gate',
        params: [
          {
            key: 'gateStyle', label: 'Gate Style', kind: 'choice', default: 'picketOverPanel',
            choices: [
              { value: 'picketOverPanel', label: 'Pickets Over Solid Panel' },
              { value: 'picket', label: 'Pickets (Full Height)' },
              { value: 'solid', label: 'Solid Plate' },
              { value: 'slat', label: 'Horizontal Slats' },
              // V-GROOVE PLANKS (2026-10-06, Matt): full-height wood planks, V-jointed
              { value: 'vPlank', label: 'Vertical V-Groove Planks' },
            ],
            conditionalOn: gateOnly,
          },
          {
            key: 'plankWidthFt', label: 'Plank Width', kind: 'length', default: 5.5 / 12, min: 2 / 12, max: 1,
            conditionalOn: { param: 'gateStyle', equals: 'vPlank' },
          },
          {
            // swing gates only — a drive gate is usually a pair, a pedestrian gate one leaf
            key: 'gateLeaves', label: 'Leaves', kind: 'choice', default: 'single',
            choices: [{ value: 'single', label: 'Single Leaf' }, { value: 'double', label: 'Double (Pair)' }],
            conditionalOn: { param: 'operation', equals: 'gateSwing' },
          },
          {
            // the solid lower panel's share of the leaf's inside height
            key: 'gatePanelPct', label: 'Lower Panel Height (%)', kind: 'number', default: 42, min: 10, max: 90,
            conditionalOn: { param: 'gateStyle', equals: 'picketOverPanel' },
          },
          {
            key: 'picketSpacingFt', label: 'Picket Spacing (On Center)', kind: 'length', default: 4.5 / 12, min: 2 / 12, max: 1,
            conditionalOn: { param: 'gateStyle', equals: ['picket', 'picketOverPanel'] },
          },
          {
            key: 'picketSizeFt', label: 'Picket Bar Size', kind: 'length', default: 0.75 / 12, min: 0.375 / 12, max: 2 / 12,
            conditionalOn: { param: 'gateStyle', equals: ['picket', 'picketOverPanel'] },
          },
          {
            key: 'slatHeightFt', label: 'Slat Height', kind: 'length', default: 5.5 / 12, min: 1 / 12, max: 1,
            conditionalOn: { param: 'gateStyle', equals: 'slat' },
          },
          {
            key: 'slatGapFt', label: 'Slat Gap', kind: 'length', default: 0.75 / 12, min: 0, max: 4 / 12,
            conditionalOn: { param: 'gateStyle', equals: 'slat' },
          },
          {
            // the leaf's own perimeter tube — the gate's construction, NOT a door frame
            key: 'gateFrameFt', label: 'Perimeter Tube Width', kind: 'length', default: 2 / 12, min: 1 / 12, max: 6 / 12,
            conditionalOn: gateOnly,
          },
          {
            key: 'gateDepthFt', label: 'Gate Thickness', kind: 'length', default: 2 / 12, min: 1 / 12, max: 6 / 12,
            conditionalOn: gateOnly,
          },
          {
            key: 'gateClearanceFt', label: 'Clearance Above Grade', kind: 'length', default: 2 / 12, min: 0, max: 1,
            conditionalOn: gateOnly,
          },
          {
            // sliding gates: how far the panel laps behind the wall at EACH jamb when closed
            key: 'gateOverlapFt', label: 'Overlap Behind Wall', kind: 'length', default: 0.5, min: 0, max: 3,
            conditionalOn: { param: 'operation', equals: 'gateSlide' },
          },
          {
            // sliding gates: the air gap between the wall face and the panel
            key: 'gateWallClearFt', label: 'Gap to Wall Face', kind: 'length', default: 1.5 / 12, min: 0.5 / 12, max: 0.5,
            conditionalOn: { param: 'operation', equals: 'gateSlide' },
          },
        ],
      },
      {
        label: 'Appearance',
        params: [
          // P6a (08 Part 1): the ONE stored appearance hook for non-assembly
          // categories — '' = the built-in default look (pre-P6a behavior)
          { key: 'surfaceMaterial', label: 'Material', kind: 'material', default: '' },
          // DOOR LEAF round (2026-10-07): construction (frameSystem) and glass (panel style) are
          // per DOOR now — the type is its operation. The old type `frameSystem` /
          // `frameProfile` / `glazing` / `panelStyle` / sidelight + transom params were folded
          // onto every placed door at doc open (doorLeafMigration.ts) and dropped from types.
        ],
      },
      {
        // FLANGE-MOUNT round (2026-09-20, openingFrame.ts#resolveFrameMount):
        // where the frame sits in the wall's depth. Default 'centered' is the
        // EXACT pre-round behavior (zero migration for every existing type —
        // most wall types in real projects never had their own `function`
        // touched beyond the wall schema's own default, so defaulting this to
        // 'auto' would have silently flange-mounted openings across existing
        // models the moment this shipped). 'auto' reads the host wall's own
        // Function at draw time instead — an exterior wall flange-mounts to
        // the outside face with a jamb extension; anything else stays
        // centered — and is meant to be opted into per curated type, not the
        // schema default. `interiorFinishDefault` only matters once the
        // resolved mount is full-depth (centered/interior) — meaningless on a
        // flange-mounted exterior frame, where there's a jamb extension
        // instead of a casing/kerf transition. Instance overrides live
        // alongside Placement below.
        label: 'Installation',
        params: [
          {
            key: 'frameMountDefault', label: 'Frame Mount', kind: 'choice', default: 'centered',
            choices: [
              { value: 'auto', label: 'Auto (from host wall)' },
              { value: 'exterior', label: 'Exterior Face (Flange-Mounted)' },
              { value: 'centered', label: 'Centered in Wall' },
              { value: 'interior', label: 'Interior Face' },
            ],
            conditionalOn: notGate,
          },
          {
            key: 'interiorFinishDefault', label: 'Interior Finish', kind: 'choice', default: 'casing',
            choices: [
              { value: 'casing', label: 'Casing' },
              { value: 'kerf', label: 'Kerf Jamb (No Casing)' },
            ],
            conditionalOn: notGate,
          },
        ],
      },
      // EXTERIOR TRIM (2026-10-02) — style + firm-standard sizes; see exteriorTrim.ts.
      // SITE GATES: a frameless gate has nothing to case — the style hides on gates
      // (its sizes key off the style, so they follow)
      {
        ...EXTERIOR_TRIM_TYPE_GROUP,
        params: EXTERIOR_TRIM_TYPE_GROUP.params.map((d) => (d.conditionalOn ? d : { ...d, conditionalOn: notGate })),
      },
      {
        // DOOR SCHEDULE COLUMNS round (2026-08-18, Matt): the firm's printed door
        // schedule carries spec fields no existing param covers — panel/frame
        // material+finish as SPEC TEXT (distinct from `surfaceMaterial`, the
        // viewport/render appearance hook, and `frameProfile`, a geometry choice)
        // plus the glazing makeup, SHGC, and U-Factor. Curated per TYPE (a
        // firm-standard door type carries one spec, same as Frame Profile above)
        // — free text for now, matching the firm's existing schedule vocabulary
        // (CLAD, SCW, FF/STN, DUAL LOW-E/CLR…) rather than a picklist neither the
        // firm nor this schema has settled yet. Blank reads as "—" on the printed
        // schedule (openingSchedule.js).
        label: 'Schedule Data',
        params: [
          { key: 'panelMaterial', label: 'Panel Material', kind: 'text', default: '' },
          { key: 'panelFinish', label: 'Panel Finish', kind: 'text', default: '' },
          { key: 'glazingSpec', label: 'Glazing Spec', kind: 'text', default: '' },
          { key: 'frameMaterial', label: 'Frame Material', kind: 'text', default: '' },
          { key: 'frameFinish', label: 'Frame Finish', kind: 'text', default: '' },
          { key: 'shgc', label: 'SHGC', kind: 'text', default: '' },
          { key: 'uFactor', label: 'U-Factor', kind: 'text', default: '' },
          // DOOR REALISM round (Matt's resolved Q6 — type default + instance
          // override): a curated door TYPE names its default hardware SET (the
          // specs tool's Hardware schedule catalog is literally keyed by door use
          // type). An instance with a blank `hardwareGroup` reads this at
          // schedule/derivation time (openingConfig.ts); a picked instance set
          // always wins. Free text matching the catalog set name — the
          // hardwareGroup convention.
          { key: 'defaultHardwareSet', label: 'Default Hardware Set', kind: 'text', default: '' },
        ],
      },
    ],
  },
  instanceParams: {
    groups: [
      {
        // OPENING CONFIGURATION GOVERNANCE round: widthFt/heightFt are INSTANCE now —
        // many sizes per type, constrained to the type's curated `sizeSeries` (a
        // corner slider's widthFt is its LEG A width; legBFt stays the type's own
        // param). `hidden` — a dedicated size-dropdown control (OpeningSizeField)
        // renders both fields together instead of the generic per-field form row (the
        // wall `flipped` convention: real data, dedicated interaction).
        label: 'Size',
        params: [
          { key: 'widthFt', label: 'Width', kind: 'length', default: 3, min: 1, max: 40, hidden: true },
          { key: 'heightFt', label: 'Height', kind: 'length', default: 6 + 8 / 12, min: 1, max: 16, hidden: true },
          // DOOR REALISM round: which frame make/model this instance uses, by NAME
          // from the type's curated frameSeries ('' = the series' first entry, the
          // sizeSeries first-entry convention; no series = the built-in default).
          // `hidden` — a dedicated dropdown in the settings modal, never free text.
          { key: 'frameProduct', label: 'Frame Product', kind: 'text', default: '', hidden: true },
        ],
      },
      {
        // HEAD SHAPE PER INSTANCE (2026-10-30, Matt): the head is per DOOR now, not per type
        // (migrateOpeningHeadsToInstance seeds existing doors from their type at doc open).
        // Only operations that still open under a shaped head take one
        // (openingOps.ts#DOOR_OPS_WITH_HEAD_SHAPE) — sliders, pockets, bifolds and overhead
        // doors resolve flat. No raked door: the leaf builds arched/segmental tops only.
        label: 'Head',
        params: [
          {
            key: 'headType', label: 'Head Shape', kind: 'choice', default: 'flat',
            choices: DOOR_HEAD_TYPES.map((o) => ({ value: o.value, label: o.label })),
            conditionalOn: { param: 'operation', equals: [...DOOR_OPS_WITH_HEAD_SHAPE] },
          },
          {
            key: 'headRiseFt', label: 'Arch Rise', kind: 'length', default: 0.5, min: 0.1, max: 4,
            conditionalOn: { param: 'headType', equals: ['arched', 'segmental'] },
          },
        ],
      },
      {
        // DOOR REALISM round (Matt: handles on/off, exterior and interior controlled
        // separately — "closets for example may not have an internal lever - up to
        // the user"): per-SIDE handle kind, 'none' = off for that side. Defaults
        // lever/lever (Matt's resolved Q5 — the firm catalog's dominant value);
        // picking a hardware set in the settings modal re-seeds both via HANDLE_MAP,
        // still freely overridable after. Convention: INTERIOR = the swing (facing)
        // side of the leaf, EXTERIOR = the opposite face.
        label: 'Hardware',
        params: [
          {
            key: 'handleExterior', label: 'Exterior Handle', kind: 'choice', default: 'lever',
            choices: HANDLE_TYPES.map((h) => ({ value: h.value, label: h.label })),
            conditionalOn: { param: 'operation', equals: [...DOOR_OPS_WITH_HANDLES] },
          },
          {
            key: 'handleInterior', label: 'Interior Handle', kind: 'choice', default: 'lever',
            choices: HANDLE_TYPES.map((h) => ({ value: h.value, label: h.label })),
            conditionalOn: { param: 'operation', equals: [...DOOR_OPS_WITH_HANDLES] },
          },
          // DOOR HARDWARE round (2026-10-09): this door's hardware look + finish — '' = the
          // project's (Project Settings / its style pack), else by construction (doorHardware.ts).
          // Picked with pictures in Door Settings, hence hidden here.
          {
            key: 'hardwareLook', label: 'Hardware Look', kind: 'choice', default: '', hidden: true,
            choices: [{ value: '', label: '(Project Style)' }, ...HARDWARE_LOOKS.map((l) => ({ value: l.value, label: l.label }))],
          },
          {
            key: 'hardwareFinish', label: 'Hardware Finish', kind: 'choice', default: '', hidden: true,
            choices: [{ value: '', label: '(Project Style)' }, ...DOOR_HARDWARE_FINISHES.map((f) => ({ value: f.value, label: f.label }))],
          },
        ],
      },
      {
        // muntins moved to INSTANCE alongside width/height (2026-08-10): a full-lite
        // door with vs. without grilles is the same product — cross-schema
        // conditionalOn the TYPE's `glazing`, the same mechanism hand/facing already
        // use against `operation`.
        label: 'Glazing',
        params: [
          {
            key: 'muntinPattern', label: 'Muntin Pattern', kind: 'choice', default: 'none',
            choices: [{ value: 'none', label: 'None' }, { value: 'grid', label: 'Grid' }],
            // DOOR LEAF round: always live — glass is the door's own panel style now, resolved
            // after the schema (openingStyle.ts#applyDoorStyle writes 'none' on a solid door, and
            // doc.ts drops these from the active keys there)
          },
          {
            key: 'muntinRows', label: 'Muntin Rows', kind: 'number', default: 3, min: 1, max: 12,
            conditionalOn: { param: 'muntinPattern', equals: 'grid' },
          },
          {
            key: 'muntinCols', label: 'Muntin Columns', kind: 'number', default: 1, min: 1, max: 12,
            conditionalOn: { param: 'muntinPattern', equals: 'grid' },
          },
        ],
      },
      {
        // Per-instance-size/muntins follow-up (2026-08-19): width + an independent
        // muntin pattern per SIDE — cross-schema conditionalOn the TYPE's
        // sidelightLeft/sidelightRight (does that side exist at all), the same
        // mechanism the door's own muntinPattern above uses against `glazing`.
        label: 'Sidelights',
        params: [
          // DOOR LEAF round (2026-10-07): WHETHER a side has a sidelight is per door now too —
          // the type is its operation (they were TYPE params; doorLeafMigration.ts moved them)
          { key: 'sidelightLeft', label: 'Sidelight — Left', kind: 'bool', default: false, conditionalOn: notGate },
          { key: 'sidelightRight', label: 'Sidelight — Right', kind: 'bool', default: false, conditionalOn: notGate },
          {
            key: 'sidelightLeftWidthFt', label: 'Left Sidelight Width', kind: 'length', default: 1.25, min: 0.5, max: 4,
            conditionalOn: { param: 'sidelightLeft', equals: true },
          },
          {
            key: 'sidelightLeftMuntinPattern', label: 'Left Sidelight Muntins', kind: 'choice', default: 'none',
            choices: [{ value: 'none', label: 'None' }, { value: 'grid', label: 'Grid' }],
            conditionalOn: { param: 'sidelightLeft', equals: true },
          },
          {
            key: 'sidelightLeftMuntinRows', label: 'Left Sidelight Muntin Rows', kind: 'number', default: 3, min: 1, max: 12,
            conditionalOn: { param: 'sidelightLeftMuntinPattern', equals: 'grid' },
          },
          {
            key: 'sidelightLeftMuntinCols', label: 'Left Sidelight Muntin Columns', kind: 'number', default: 1, min: 1, max: 12,
            conditionalOn: { param: 'sidelightLeftMuntinPattern', equals: 'grid' },
          },
          {
            key: 'sidelightRightWidthFt', label: 'Right Sidelight Width', kind: 'length', default: 1.25, min: 0.5, max: 4,
            conditionalOn: { param: 'sidelightRight', equals: true },
          },
          {
            key: 'sidelightRightMuntinPattern', label: 'Right Sidelight Muntins', kind: 'choice', default: 'none',
            choices: [{ value: 'none', label: 'None' }, { value: 'grid', label: 'Grid' }],
            conditionalOn: { param: 'sidelightRight', equals: true },
          },
          {
            key: 'sidelightRightMuntinRows', label: 'Right Sidelight Muntin Rows', kind: 'number', default: 3, min: 1, max: 12,
            conditionalOn: { param: 'sidelightRightMuntinPattern', equals: 'grid' },
          },
          {
            key: 'sidelightRightMuntinCols', label: 'Right Sidelight Muntin Columns', kind: 'number', default: 1, min: 1, max: 12,
            conditionalOn: { param: 'sidelightRightMuntinPattern', equals: 'grid' },
          },
        ],
      },
      {
        // Per-instance-size/muntins follow-up: same treatment for the transom —
        // height + its own independent muntin pattern, conditionalOn the TYPE's
        // `transom` (does it exist at all).
        label: 'Transom',
        params: [
          { key: 'transom', label: 'Transom', kind: 'bool', default: false, conditionalOn: notGate },
          {
            key: 'transomHeightFt', label: 'Transom Height', kind: 'length', default: 1, min: 0.5, max: 4,
            conditionalOn: { param: 'transom', equals: true },
          },
          {
            // TRANSOM OVER EACH (2026-10-30, Matt): with sidelights, one transom across the door
            // and both sidelights, or a separate transom over each (flat heads only)
            key: 'transomSplit', label: 'Transom Over Sidelights', kind: 'choice', default: 'full',
            choices: [{ value: 'full', label: 'One across' }, { value: 'split', label: 'Separate over each' }],
            conditionalOn: { param: 'transom', equals: true },
          },
          {
            // 'lined' (2026-10-30, Matt): a bar over every vertical line below — the joins, the
            // meeting stiles, each leaf's and sidelight's muntins (bim-geometry transomLines.ts)
            key: 'transomMuntinPattern', label: 'Transom Muntins', kind: 'choice', default: 'none',
            choices: [{ value: 'none', label: 'None' }, { value: 'lined', label: 'Lined up with below' }, { value: 'grid', label: 'Even grid' }],
            conditionalOn: { param: 'transom', equals: true },
          },
          {
            key: 'transomMuntinRows', label: 'Transom Muntin Rows', kind: 'number', default: 1, min: 1, max: 12,
            conditionalOn: { param: 'transomMuntinPattern', equals: ['grid', 'lined'] },
          },
          {
            key: 'transomMuntinCols', label: 'Transom Muntin Columns', kind: 'number', default: 3, min: 1, max: 12,
            conditionalOn: { param: 'transomMuntinPattern', equals: 'grid' },
          },
        ],
      },
      {
        // SHUTTERS ON DOORS (2026-10-30, Matt): the window's side-hung shutters on a door —
        // never with a sidelight (nowhere for an open leaf to stand), never on an overhead door
        // or gate (openingOps.ts#DOOR_OPS_WITH_SHUTTERS), never a Bahama (nobody walks under a
        // top-hung shutter). With a transom the door and the transom each get their own stacked
        // leaves (bim-geometry wall/shutters.ts). Must come AFTER Sidelights + Transom: the
        // conditions read those resolved values.
        label: 'Shutters',
        params: [
          {
            key: 'shutterStyle', label: 'Shutters', kind: 'choice', default: '',
            choices: [
              { value: '', label: 'None' },
              { value: 'louvered', label: 'Louvered' },
              { value: 'raisedPanel', label: 'Raised Panel' },
              { value: 'boardBatten', label: 'Board & Batten' },
            ],
            conditionalOn: [
              { param: 'operation', equals: [...DOOR_OPS_WITH_SHUTTERS] },
              { param: 'sidelightLeft', equals: false },
              { param: 'sidelightRight', equals: false },
            ],
          },
          {
            key: 'shutterSides', label: 'Sides', kind: 'choice', default: 'both',
            choices: [{ value: 'both', label: 'Both Sides (pair)' }, { value: 'one', label: 'One Side' }],
            conditionalOn: { param: 'shutterStyle', equals: ['louvered', 'raisedPanel', 'boardBatten'] },
          },
          {
            key: 'shutterSide', label: 'Side', kind: 'choice', default: 'L',
            choices: [{ value: 'L', label: 'Left (from outside)' }, { value: 'R', label: 'Right (from outside)' }],
            conditionalOn: [
              { param: 'shutterStyle', equals: ['louvered', 'raisedPanel', 'boardBatten'] },
              { param: 'shutterSides', equals: 'one' },
            ],
          },
          {
            key: 'shutterState', label: 'Position', kind: 'choice', default: 'open',
            choices: [{ value: 'open', label: 'Open' }, { value: 'closed', label: 'Closed (covers the door)' }],
            conditionalOn: { param: 'shutterStyle', equals: ['louvered', 'raisedPanel', 'boardBatten'] },
          },
          {
            key: 'shutterRails', label: 'Middle Rails', kind: 'number', default: 1, min: 0, max: 6,
            conditionalOn: { param: 'shutterStyle', equals: ['louvered', 'raisedPanel'] },
          },
          {
            key: 'shutterMaterial', label: 'Shutter Material', kind: 'material', default: '',
            conditionalOn: { param: 'shutterStyle', equals: ['louvered', 'raisedPanel', 'boardBatten'] },
          },
          { key: 'shutterColor', label: 'Shutter Color', kind: 'data', default: null, hidden: true },
        ],
      },
      {
        // PIVOT LOCATION (2026-10-08, Matt): the clear passage a pivot door gives when open —
        // the pivot axis is DERIVED from it and the door's width (openingOps.ts#pivotOffsetFt).
        // Blank = the default 4" offset. Edited in Door Settings.
        label: 'Pivot',
        params: [
          {
            key: 'pivotPassageFt', label: 'Passage Width', kind: 'length', default: null, optional: true,
            optionalHint: 'pivot 4" from the jamb', min: 0.5, max: 20,
            conditionalOn: { param: 'operation', equals: 'pivot' },
          },
        ],
      },
      {
        // 2026-08-13 (Matt: "door thresholds should be toggleable at interior
        // doors"): a per-instance decision, not a type — the SAME curated door
        // sits at a bath sill with a threshold and at a closet flush without
        // one. Purely additive box geometry at the frame's own footprint (see
        // bim-geometry's protos.ts) — never interacts with the leaf/operation,
        // so it's available on every operation EXCEPT corner sliders (their own
        // bespoke two-host proto builder has no capture/threshold path — the
        // same recorded v1 limit their walk-animation exclusion already has).
        label: 'Threshold',
        params: [
          { key: 'thresholdEnabled', label: 'Threshold', kind: 'bool', default: false, conditionalOn: notGate },
        ],
      },
      {
        label: 'Placement',
        params: [
          { key: 'hostOffsetFt', label: 'Offset Along Wall', kind: 'length', default: 0, min: 0, positional: true },
          // 2026-08-09 (Matt): a door raised off the floor — shower doors, bifold
          // pass-throughs set at counter height, mechanical-closet doors on a
          // platform. Universal (not operation-gated: any leaf can be raised).
          // Default 0 preserves every existing door's floor-set behavior with no
          // migration. `sillProfile` (openings.ts) already fills the wall SOLID
          // below a raised opening — the same piece a window's sill uses, no new
          // geometry code needed. Positional (moves the leaf, doesn't restyle it) —
          // excluded from eyedropper/inject like hostOffsetFt.
          { key: 'sillFt', label: 'Height Above Floor', kind: 'length', default: 0, min: 0, max: 10, positional: true },
          // hand/facing applicability is per OPERATION (cross-schema conditionalOn —
          // the instance schema resolves with the type params as context): a french
          // pair or bypass slider has no hand; a pocket leaf has no facing
          {
            // FLIP HANDLES round (Matt, 2026-09-21): "there is not a high return on
            // investment if we make the terminology work better — ultimately what
            // matters is the way the symbol is depicted on the plan" — hand/facing
            // are still real, resolved, undoable params (every geometry/schedule
            // consumer reads them unchanged), but the ONLY way to edit them is now
            // the two flip icons on the plan symbol (or Spacebar to cycle), same
            // doctrine as a wall's `flipped` above.
            key: 'hand', label: 'Hand', kind: 'choice', default: 'L', hidden: true,
            choices: [{ value: 'L', label: 'Left' }, { value: 'R', label: 'Right' }],
            conditionalOn: { param: 'operation', equals: [...DOOR_OPS_WITH_HAND] },
          },
          {
            key: 'facing', label: 'Swing Facing', kind: 'choice', default: 'in', hidden: true,
            choices: [{ value: 'in', label: 'In' }, { value: 'out', label: 'Out' }],
            conditionalOn: { param: 'operation', equals: [...DOOR_OPS_WITH_FACING] },
          },
          // PANEL COUNT PER INSTANCE (2026-10-06): each multi-slide / multi-panel bifold /
          // corner slider sets its own panel count — blank follows the type's starting
          // count (doc.ts folds the effective count over the type's, so the 3D body, plan
          // symbol, elevation figure and schedules all read one number)
          {
            key: 'panelCount', label: 'Panel Count', kind: 'number', default: null, optional: true, min: 2, max: 8,
            conditionalOn: { param: 'operation', equals: [...DOOR_OPS_WITH_PANEL_COUNT] },
          },
          {
            key: 'panelCountB', label: 'Leg B Panel Count', kind: 'number', default: null, optional: true, min: 1, max: 8,
            conditionalOn: { param: 'operation', equals: 'cornerSlider' },
          },
          {
            // Tier 2: the multi-panel families' park/fold side — 'split' covers the
            // taxonomy's both/split (biparting) case, see openingOps.ts header
            key: 'direction', label: 'Travel Direction', kind: 'choice', default: 'L',
            choices: [
              { value: 'L', label: 'Left' },
              { value: 'R', label: 'Right' },
              { value: 'split', label: 'Split — Both Ways' },
            ],
            conditionalOn: { param: 'operation', equals: [...DOOR_OPS_WITH_DIRECTION] },
          },
          {
            // corner slider only: the LEG B host wall's element id (hostId = leg A).
            // Set by placement, never hand-edited; positional so eyedropper/inject
            // can never copy it (it would MOVE the target onto this unit's walls).
            key: 'hostBId', label: 'Corner Wall B', kind: 'text', default: '',
            readonly: true, positional: true,
            conditionalOn: { param: 'operation', equals: 'cornerSlider' },
          },
          // Phase 5 tags (07 Part 3): the ONE stored tag pin — empty = the derived
          // per-instance door number (kernel tags.ts); doors pin PER INSTANCE
          // (door-schedule convention: every leaf has its own number)
          { key: 'tagOverride', label: 'Tag Override', kind: 'text', default: '' },
          // FLANGE-MOUNT round: per-instance overrides of the type's own
          // Installation defaults above — '' inherits the type (the
          // frameProduct/hardwareGroup shadow pattern). A one-off condition
          // (a retrofit, an unusual rough opening) never forces a second type.
          {
            // 'auto' deliberately not offered here — that's what leaving this
            // blank already does when the type default is 'auto' (or IS the
            // type default itself); an instance override always names a real
            // mount, never re-requests auto-derivation.
            key: 'frameMount', label: 'Frame Mount Override', kind: 'choice', default: '',
            choices: [
              { value: '', label: '(Use Type Default)' },
              { value: 'exterior', label: 'Exterior Face (Flange-Mounted)' },
              { value: 'centered', label: 'Centered in Wall' },
              { value: 'interior', label: 'Interior Face' },
            ],
            conditionalOn: notGate,
          },
          // PROJECT STYLE phase 2: see the window schema's identical params
          { key: 'frameRecessFt', label: 'Recess From Exterior Face', kind: 'length', default: null, optional: true, optionalHint: 'project standard', min: 0, max: 2, conditionalOn: notGate },
          // DOOR LEAF round: the door's CONSTRUCTION — sets its sightlines (leafLayout.ts) and the
          // schedule's materials. Picked with pictures in Door Settings, hence hidden here.
          { key: 'frameSystem', label: 'Construction', kind: 'choice', default: '', hidden: true, choices: [
              { value: '', label: '(Project Style)' },
              { value: 'aluminum', label: 'Aluminum' },
              { value: 'clad', label: 'Aluminum-Clad Wood' },
              { value: 'wood', label: 'Wood' },
              { value: 'steel', label: 'Steel' },
              { value: 'fiberglass', label: 'Fiberglass' },
            ], conditionalOn: notGate },
          // PROJECT STYLE phase 3: the door's condition (blank = inferred from its operation and
          // its wall) and a one-off panel style
          { key: 'doorCondition', label: 'Condition', kind: 'choice', default: '', choices: [
              { value: '', label: '(Automatic)' },
              { value: 'entry', label: 'Exterior Entry' },
              { value: 'patio', label: 'Exterior Patio' },
              { value: 'exteriorFrench', label: 'Exterior French' },
              { value: 'passage', label: 'Interior Passage' },
              { value: 'interiorFrench', label: 'Interior French' },
              { value: 'closet', label: 'Closet' },
              { value: 'garage', label: 'Garage' },
              { value: 'utility', label: 'Utility / Service' },
            ], conditionalOn: notGate },
          // DOOR LEAF round: picked with pictures in Door Settings. PANEL STYLE PER DOOR (2026-10-12,
          // Matt: "doesn't let me change the panel style ... as an instance parameter") — shown in
          // the Properties panel too, not only behind the Settings button.
          { key: 'panelStyle', label: 'Panel Style', kind: 'choice', default: '', choices: [
              { value: '', label: '(Project Style)' }, ...[
              { value: 'flush', label: 'Flat (Flush)' },
              { value: 'shaker1', label: 'Shaker, 1 Panel' },
              { value: 'shaker2', label: 'Shaker, 2 Panel' },
              { value: 'shaker3', label: 'Shaker, 3 Panel' },
              { value: 'shaker5', label: 'Shaker, 5 Panel' },
              { value: 'raised1', label: 'Raised, 1 Panel' },
              { value: 'raised2', label: 'Raised, 2 Panel' },
              { value: 'raised3', label: 'Raised, 3 Panel' },
              { value: 'raised5', label: 'Raised, 5 Panel' },
              { value: 'plank', label: 'Plank (Vertical Boards)' },
              { value: 'halfLite', label: 'Half-Lite' },
              { value: 'fullLite', label: 'Full-Lite' },
              { value: 'frenchLite', label: 'Divided-Lite French' },
            ],
            ], conditionalOn: takesPanel },
          // DUTCH LOWER LEAF (2026-10-12, Matt: "a glazed top and a solid bottom"): the lower
          // leaf's own style — blank = the same as the upper (Panel Style above)
          { key: 'panelStyleLower', label: 'Lower Leaf Panel Style', kind: 'choice', default: '', choices: [
              { value: '', label: '(Same as Upper)' },
              { value: 'flush', label: 'Flat (Flush)' },
              { value: 'shaker1', label: 'Shaker, 1 Panel' },
              { value: 'shaker2', label: 'Shaker, 2 Panel' },
              { value: 'shaker3', label: 'Shaker, 3 Panel' },
              { value: 'shaker5', label: 'Shaker, 5 Panel' },
              { value: 'raised1', label: 'Raised, 1 Panel' },
              { value: 'raised2', label: 'Raised, 2 Panel' },
              { value: 'raised3', label: 'Raised, 3 Panel' },
              { value: 'raised5', label: 'Raised, 5 Panel' },
              { value: 'plank', label: 'Plank (Vertical Boards)' },
              { value: 'halfLite', label: 'Half-Lite' },
              { value: 'fullLite', label: 'Full-Lite' },
              { value: 'frenchLite', label: 'Divided-Lite French' },
            ], conditionalOn: { param: 'operation', equals: 'dutch' } },
          // PANEL STYLE PER GATE (2026-10-12): a gate's style is its type's unless this gate says
          // otherwise — SHADOWS the type key; doc.ts resolveElement folds it into both params and
          // typeParams (the glazing mirror), so the 3D leaf, plan, elevation and schedule agree
          { key: 'gateStyle', label: 'Gate Style', kind: 'choice', default: '', choices: [
              { value: '', label: '(Type)' },
              { value: 'picketOverPanel', label: 'Pickets Over Solid Panel' },
              { value: 'picket', label: 'Pickets (Full Height)' },
              { value: 'solid', label: 'Solid Plate' },
              { value: 'slat', label: 'Horizontal Slats' },
              { value: 'vPlank', label: 'Vertical V-Groove Planks' },
            ], conditionalOn: gateOnly },
          { key: 'frameColorExterior', label: 'Frame Color (Exterior)', kind: 'data', default: null, hidden: true },
          { key: 'frameColorInterior', label: 'Frame Color (Interior)', kind: 'data', default: null, hidden: true },
          // FRONT DOOR (phase 3): an entry door's slab color (a colors.ts ref) — blank = the role
          { key: 'leafColor', label: 'Door Color', kind: 'data', default: null, hidden: true },
          // PAINT BRUSH (2026-10-07): the brushed color (a colors.ts ref) — blank = its finish / role
          { key: 'trimColor', label: 'Trim Color (Exterior)', kind: 'data', default: null, hidden: true },
          { key: 'interiorTrimColor', label: 'Trim Color (Interior)', kind: 'data', default: null, hidden: true },
          // EXTERIOR TRIM (2026-10-02): this opening's own trim — '' = the type's
          { ...EXTERIOR_TRIM_INSTANCE_PARAM, conditionalOn: notGate },
          { ...BRICK_LINTEL_INSTANCE_PARAM, conditionalOn: [notGate, BRICK_LINTEL_INSTANCE_PARAM.conditionalOn] },
          // INTERIOR TRIM (2026-10-04): casings on the room side(s) — both faces in an
          // interior wall; gates have nothing to case
          ...INTERIOR_TRIM_INSTANCE_PARAMS.map((d) => ({
            ...d,
            conditionalOn: 'conditionalOn' in d && d.conditionalOn ? [notGate, d.conditionalOn] : notGate,
          })),
          {
            key: 'interiorFinish', label: 'Interior Finish Override', kind: 'choice', default: '',
            choices: [
              { value: '', label: '(Use Type Default)' },
              { value: 'casing', label: 'Casing' },
              { value: 'kerf', label: 'Kerf Jamb (No Casing)' },
            ],
            conditionalOn: notGate,
          },
        ],
      },
      {
        // DOOR SCHEDULE COLUMNS round: Hardware Group and Egress are per-INSTANCE,
        // unlike the Schedule Data type params above — two doors of the same
        // curated type can carry different hardware sets or egress status (a
        // French pair type used at both a bedroom exit and a closet, say).
        label: 'Schedule Data',
        params: [
          { key: 'hardwareGroup', label: 'Hardware Group', kind: 'text', default: '' },
          { key: 'egress', label: 'Egress Door', kind: 'bool', default: false },
        ],
      },
    ],
  },
}
